import { useState } from 'preact/hooks';
import type { PrActionBody, PrDetailBody } from '../../shared/pr/api';
import { useI18n } from '../i18n';
import { ApiError, apiSend } from '../lib/api';

// The requester cancels their own request (D82): a draft, one returned to them, or one still
// waiting for Procurement's review. Asks for a reason and a second click; the broker decides.
export function CancelRequest({ pr, onDone }: { pr: PrDetailBody; onDone: () => void }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  if (!open) {
    return (
      <div class="actions">
        <button type="button" class="button button--danger" onClick={() => setOpen(true)}>
          {t('cancel.open')}
        </button>
      </div>
    );
  }

  async function cancel() {
    if (!reason.trim()) return setProblem(t('cancel.reasonRequired'));
    setBusy(true);
    setProblem(null);
    try {
      await apiSend<PrActionBody>('POST', `/api/prs/${encodeURIComponent(pr.prNo)}/action`, {
        action: 'cancel',
        etag: pr.etag,
        reason,
      });
      onDone();
    } catch (err) {
      const e = err instanceof ApiError ? err : null;
      setProblem(
        t(e?.code === 'conflict' ? 'cancel.conflict' : 'cancel.failed') +
          (e?.correlationId ? ` ${t('error.reference', { ref: e.correlationId })}` : ''),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <section class="card stack" aria-labelledby="cancel-title">
      <h2 id="cancel-title" class="card__title">
        {t('cancel.title')}
      </h2>
      <p>{t('cancel.explain')}</p>
      <div class="field">
        <label for="cancel-reason">{t('cancel.reason')}</label>
        <textarea
          id="cancel-reason"
          maxLength={2000}
          value={reason}
          onInput={(e) => setReason((e.target as HTMLTextAreaElement).value)}
        />
      </div>
      {problem && (
        <p class="form-problem" role="alert">
          {problem}
        </p>
      )}
      <div class="actions">
        <button type="button" class="button" disabled={busy} onClick={() => setOpen(false)}>
          {t('cancel.keep')}
        </button>
        <button
          type="button"
          class="button button--danger"
          disabled={busy}
          onClick={() => void cancel()}
        >
          {t('cancel.confirm')}
        </button>
      </div>
    </section>
  );
}
